import React from 'react';
import { H5, H6 } from '../../../../AbstractElements';
import { FormGroup, Input, Label, FormFeedback, Row, Col } from 'reactstrap';
import PropTypes from 'prop-types';

const DocumentRequestForm = ({ formData, errors, setFormData, handleInputChange, handleCounterPartyTypeChange, workFileTypes, insuranceCompanies, fieldPermissions = [], canEdit = true }) => {

    const getFieldAccess = (fieldName) => {
        // If canEdit is false, all fields should be disabled
        if (!canEdit) {
            return {
                editable: false,
                required: false,
            };
        }

        const permission = fieldPermissions.find(p => p.fieldName === fieldName);
        return {
            editable: permission ? permission.access === 'Editable' : true, // Default to editable
            required: permission ? permission.required : false, // Default to not required
        };
    };

    // Define field access using the updated API structure
    // Alan adlarının API yanıtıyla eşleştiğinden emin olun!
    const isOpenDamageFileAccess = getFieldAccess('licenseplates.isthereopendamagefile');
    const docRequestDateAccess = getFieldAccess('accidents.documentrequestdate');
    const docReceivedDateAccess = getFieldAccess('accidents.documentreceiveddate');
    const docRequestStatusAccess = { editable: false, required: false }; // API'de izni yoksa manuel olarak ReadOnly
    const ourFaultRateAccess = getFieldAccess('accidents.ourfaultrate');
    const counterpartyFaultRateAccess = getFieldAccess('accidents.counterpartyfaultrate'); // Hep readOnly
    const counterpartyTypeAccess = getFieldAccess('accidents.counterpartytype');
    const counterpartyIdAccess = getFieldAccess('accidents.counterpartyidentificationnumber');
    const counterpartyTaxAccess = getFieldAccess('accidents.counterpartytaxnumber');
    const counterpartyOwnerAccess = getFieldAccess('accidents.counterpartycarowner');
    const counterpartyPlateAccess = getFieldAccess('accidents.counterpartylicenseplatenumber');
    const counterpartyAddressAccess = getFieldAccess('accidents.counterpartyaddress');
    const clientTrafficAccess = getFieldAccess('licenseplates.trafficinsurancepolicy');
    const clientCascoAccess = getFieldAccess('licenseplates.cascoinsurancepolicy');
    const cpTrafficAccess = getFieldAccess('accidents.counterpartytrafficinsurancepolicy');
    const cpCascoAccess = getFieldAccess('accidents.counterpartycascoinsurancepolicy');
    // --- YENİ: Başlangıç Tarihi İzinleri ---
    const clientTrafficStartAccess = getFieldAccess('licenseplates.trafficinsurancestart');
    const clientCascoStartAccess = getFieldAccess('licenseplates.cascoinsurancestart');
    const cpTrafficStartAccess = getFieldAccess('accidents.counterpartytrafficinsurancestart');
    const cpCascoStartAccess = getFieldAccess('accidents.counterpartycascoinsurancestart');
    // --- BİTTİ: Başlangıç Tarihi İzinleri ---

    // Add special handlers for insurance provider selection - similar to the district handler in AddressInfoForm
    const handleClientTrafficInsuranceProviderChange = (e) => {
        const providerId = e.target.value;
        handleInputChange({
            target: {
                name: 'trafficInsurancePolicy',
                value: {
                    ...formData.trafficInsurancePolicy,
                    trafficInsuranceProvider: providerId === "" ? [] : [providerId]
                }
            }
        });
    };

    const handleClientCascoInsuranceProviderChange = (e) => {
        const providerId = e.target.value;
        handleInputChange({
            target: {
                name: 'cascoInsurancePolicy',
                value: {
                    ...formData.cascoInsurancePolicy,
                    cascoInsuranceProvider: providerId === "" ? [] : [providerId]
                }
            }
        });
    };

    const handleCounterpartyTrafficInsuranceProviderChange = (e) => {
        const providerId = e.target.value;
        handleInputChange({
            target: {
                name: 'counterpartyTrafficInsurancePolicy',
                value: {
                    ...formData.counterpartyTrafficInsurancePolicy,
                    trafficInsuranceProvider: providerId === "" ? [] : [providerId]
                }
            }
        });
    };

    const handleCounterpartyCascoInsuranceProviderChange = (e) => {
        const providerId = e.target.value;
        handleInputChange({
            target: {
                name: 'counterpartyCascoInsurancePolicy',
                value: {
                    ...formData.counterpartyCascoInsurancePolicy,
                    cascoInsuranceProvider: providerId === "" ? [] : [providerId]
                }
            }
        });
    };

    // Sigorta numaralarını büyük harfe çeviren özel handler'lar
    const handleClientTrafficInsuranceNumberChange = (e) => {
        const upperCaseValue = e.target.value.toUpperCase();
        handleInputChange({
            target: {
                name: 'trafficInsurancePolicy',
                value: {
                    ...formData.trafficInsurancePolicy,
                    trafficInsuranceNumber: upperCaseValue
                }
            }
        });
    };

    const handleClientCascoInsuranceNumberChange = (e) => {
        const upperCaseValue = e.target.value.toUpperCase();
        handleInputChange({
            target: {
                name: 'cascoInsurancePolicy',
                value: {
                    ...formData.cascoInsurancePolicy,
                    cascoInsuranceNumber: upperCaseValue
                }
            }
        });
    };

    const handleCounterpartyTrafficInsuranceNumberChange = (e) => {
        const upperCaseValue = e.target.value.toUpperCase();
        handleInputChange({
            target: {
                name: 'counterpartyTrafficInsurancePolicy',
                value: {
                    ...formData.counterpartyTrafficInsurancePolicy,
                    trafficInsuranceNumber: upperCaseValue
                }
            }
        });
    };

    const handleCounterpartyCascoInsuranceNumberChange = (e) => {
        const upperCaseValue = e.target.value.toUpperCase();
        handleInputChange({
            target: {
                name: 'counterpartyCascoInsurancePolicy',
                value: {
                    ...formData.counterpartyCascoInsurancePolicy,
                    cascoInsuranceNumber: upperCaseValue
                }
            }
        });
    };

    return (
        <>
            <H5>Evrak Talep Bilgileri</H5>
            <div style={{ height: "20px" }}></div>

            <FormGroup className="d-flex align-items-center mb-3">
                <Label for="isThereOpenDamageFileRadio" className="me-3 text-nowrap" style={{ minWidth: '200px' }}>
                    Açık Hasar Dosyası Var Mı? {isOpenDamageFileAccess.required && <span className="text-danger">*</span>}
                </Label>
                <div className="d-flex">
                    <div className="radio radio-success me-3 d-flex align-items-center">
                        <Input
                            id="isThereOpenDamageFileTrue"
                            type="radio"
                            name="isThereOpenDamageFile"
                            value="yes"
                            checked={formData.isThereOpenDamageFile === "yes"}
                            onChange={() =>
                                setFormData((prevFormData) => ({
                                    ...prevFormData,
                                    isThereOpenDamageFile: "yes",
                                }))
                            }
                            invalid={!!errors.isThereOpenDamageFile}
                            disabled={!isOpenDamageFileAccess.editable} // CustomerInfoForm ile aynı kullanım: !editable
                            required={isOpenDamageFileAccess.required}
                        />
                        <Label for="isThereOpenDamageFileTrue" className="ms-1 mb-0">
                            Evet
                        </Label>
                    </div>
                    <div className="radio radio-danger d-flex align-items-center">
                        <Input
                            id="isThereOpenDamageFileFalse"
                            type="radio"
                            name="isThereOpenDamageFile"
                            value="no"
                            checked={formData.isThereOpenDamageFile === "no"}
                            onChange={() =>
                                setFormData((prevFormData) => ({
                                    ...prevFormData,
                                    isThereOpenDamageFile: "no",
                                }))
                            }
                            invalid={!!errors.isThereOpenDamageFile}
                            disabled={!isOpenDamageFileAccess.editable} // CustomerInfoForm ile aynı kullanım: !editable
                        />
                        <Label for="isThereOpenDamageFileFalse" className="ms-1 mb-0">
                            Hayır
                        </Label>
                    </div>
                    <FormFeedback>{errors.isThereOpenDamageFile}</FormFeedback>
                </div>
            </FormGroup>

            <FormGroup>
                <Label for="documentRequestDate">Evrak Talep Edilme Tarihi {docRequestDateAccess.required && <span className="text-danger">*</span>}</Label>
                <Input
                    type="date"
                    id="documentRequestDate"
                    name="documentRequestDate"
                    value={formData.documentRequestDate instanceof Date && !isNaN(formData.documentRequestDate)
                        ? formData.documentRequestDate.toISOString().substring(0, 10)
                        : ''}
                    onChange={(e) => {
                        const value = e.target.value ? new Date(e.target.value) : null;
                        handleInputChange({
                            target: {
                                name: 'documentRequestDate',
                                value
                            }
                        });
                    }}
                    invalid={!!errors.documentRequestDate}
                    disabled={!docRequestDateAccess.editable} // CustomerInfoForm ile aynı kullanım: !editable
                    required={docRequestDateAccess.required}
                />
                <FormFeedback>{errors.documentRequestDate}</FormFeedback>
            </FormGroup>

            <FormGroup>
                <Label for="documentRequestStatus">Evrak Talep Durumu</Label> {/* Bu alan için required yıldızı yok */}
                <Input
                    type="text"
                    id="documentRequestStatus"
                    name="documentRequestStatus"
                    value={formData.documentRequestStatus}
                    onChange={handleInputChange}
                    disabled={!docRequestStatusAccess.editable} // CustomerInfoForm ile aynı kullanım: !editable
                    invalid={!!errors.documentRequestStatus}
                />
                <FormFeedback>{errors.documentRequestStatus}</FormFeedback>
            </FormGroup>

            <FormGroup>
                <Label for="documentReceivedDate">Evrak Gelme Tarihi {docReceivedDateAccess.required && <span className="text-danger">*</span>}</Label>
                <Input
                    type="date"
                    id="documentReceivedDate"
                    name="documentReceivedDate"
                    value={formData.documentReceivedDate instanceof Date && !isNaN(formData.documentReceivedDate)
                        ? formData.documentReceivedDate.toISOString().substring(0, 10)
                        : formData.documentReceivedDate || ''}
                    onChange={(e) => {
                        const value = e.target.value ? new Date(e.target.value) : null;
                        handleInputChange({
                            target: {
                                name: 'documentReceivedDate',
                                value
                            }
                        });
                    }}
                    invalid={!!errors.documentReceivedDate}
                    disabled={!docReceivedDateAccess.editable} // CustomerInfoForm ile aynı kullanım: !editable
                    required={docReceivedDateAccess.required}
                />
                <FormFeedback>{errors.documentReceivedDate}</FormFeedback>
            </FormGroup>

            <div style={{ height: "20px" }}></div>
            <hr className="mt-4 mb-4" />
            <div style={{ height: "20px" }}></div>

            <H6>Müvekkil Sigorta Bilgileri</H6>
            <div style={{ height: "20px" }}></div>

            <FormGroup>
                <Label for="ourFaultRate">Müvekkil Kusur Oranı {ourFaultRateAccess.required && <span className="text-danger">*</span>}</Label>
                <div className="input-group">
                    <Input
                        type="text"
                        id="ourFaultRate"
                        name="ourFaultRate"
                        value={formData.ourFaultRate || ''}
                        onChange={(e) => {
                            let value = e.target.value.replace(/[^0-9]/g, '');
                            if (value && parseInt(value) > 100) {
                                value = '100';
                            }

                            // Update the client fault rate
                            handleInputChange({
                                target: {
                                    name: 'ourFaultRate',
                                    value
                                }
                            });

                            // Automatically calculate and update the counterparty fault rate
                            const counterValue = value ? (100 - parseInt(value)).toString() : '';
                            handleInputChange({
                                target: {
                                    name: 'counterpartyFaultRate',
                                    value: counterValue
                                }
                            });
                        }}
                        placeholder="0-100"
                        invalid={!!errors.ourFaultRate || (formData.ourFaultRate && (parseInt(formData.ourFaultRate) > 100 || parseInt(formData.ourFaultRate) < 0))}
                        disabled={!ourFaultRateAccess.editable} // CustomerInfoForm ile aynı kullanım: !editable
                        required={ourFaultRateAccess.required}
                    />
                    <div className="input-group-append">
                        <span className="input-group-text">%</span>
                    </div>
                    <FormFeedback>
                        {errors.ourFaultRate || 'Oran 0-100 arasında olmalıdır'}
                    </FormFeedback>
                </div>
            </FormGroup>

            <FormGroup>
                <Row>
                    <Col md={3}>
                        <FormGroup>
                            <Label className="form-label">Müvekkil Trafik Sigorta Sağlayıcısı {clientTrafficAccess.required && <span className="text-danger">*</span>}</Label>
                            <Input
                                type="select"
                                id="trafficInsuranceProvider"
                                name="trafficInsuranceProvider"
                                value={formData.trafficInsurancePolicy?.trafficInsuranceProvider?.[0] || ""}
                                onChange={handleClientTrafficInsuranceProviderChange}
                                invalid={!!errors.trafficInsuranceProvider}
                                disabled={!clientTrafficAccess.editable} // CustomerInfoForm ile aynı kullanım: !editable
                                required={clientTrafficAccess.required}
                            >
                                <option value="">Sigorta Şirketi Seçiniz</option>
                                {insuranceCompanies && insuranceCompanies.length > 0 ?
                                    insuranceCompanies.map(company => (
                                        <option key={company.id} value={company.id}>
                                            {company.name}
                                        </option>
                                    )) :
                                    <option value="" disabled>Sigorta şirketi bulunamadı</option>
                                }
                            </Input>
                            <FormFeedback>{errors.trafficInsuranceProvider}</FormFeedback>
                        </FormGroup>
                    </Col>
                    <Col md={3}>
                        <Label for="trafficInsuranceNumber">Müvekkil Trafik Sigorta Numarası {clientTrafficAccess.required && <span className="text-danger">*</span>}</Label>
                        <Input
                            type="text"
                            id="trafficInsuranceNumber"
                            name="trafficInsuranceNumber"
                            value={formData.trafficInsurancePolicy?.trafficInsuranceNumber || ''}
                            onChange={handleClientTrafficInsuranceNumberChange}
                            invalid={!!errors.trafficInsuranceNumber}
                            disabled={!clientTrafficAccess.editable} // CustomerInfoForm ile aynı kullanım: !editable
                            required={clientTrafficAccess.required}
                        />
                        <FormFeedback>{errors.trafficInsuranceNumber}</FormFeedback>
                    </Col>
                    <Col md={3}>
                        <Label for="trafficInsuranceStart">Müvekkil Trafik Başlangıç {clientTrafficStartAccess.required && <span className="text-danger">*</span>}</Label>
                        <Input
                            type="date"
                            id="trafficInsuranceStart"
                            name="trafficInsuranceStart"
                            value={formData.trafficInsurancePolicy?.trafficInsuranceStart instanceof Date && !isNaN(formData.trafficInsurancePolicy?.trafficInsuranceStart)
                                ? formData.trafficInsurancePolicy?.trafficInsuranceStart.toISOString().substring(0, 10)
                                : ''}
                            onChange={(e) => {
                                const value = e.target.value ? new Date(e.target.value) : null;
                                handleInputChange({
                                    target: {
                                        name: 'trafficInsurancePolicy',
                                        value: {
                                            ...formData.trafficInsurancePolicy,
                                            trafficInsuranceStart: value
                                        }
                                    }
                                });
                            }}
                            invalid={!!errors.trafficInsuranceStart}
                            disabled={!clientTrafficStartAccess.editable}
                            required={clientTrafficStartAccess.required}
                        />
                        <FormFeedback>{errors.trafficInsuranceStart}</FormFeedback>
                    </Col>
                    <Col md={3}>
                        <Label for="trafficInsuranceExpiry">Müvekkil Trafik Sigorta Vade {clientTrafficAccess.required && <span className="text-danger">*</span>}</Label>
                        <Input
                            type="date"
                            id="trafficInsuranceExpiry"
                            name="trafficInsuranceExpiry"
                            value={formData.trafficInsurancePolicy?.trafficInsuranceExpiry instanceof Date && !isNaN(formData.trafficInsurancePolicy?.trafficInsuranceExpiry)
                                ? formData.trafficInsurancePolicy?.trafficInsuranceExpiry.toISOString().substring(0, 10)
                                : ''}
                            onChange={(e) => {
                                const value = e.target.value ? new Date(e.target.value) : null;
                                handleInputChange({
                                    target: {
                                        name: 'trafficInsurancePolicy',
                                        value: {
                                            ...formData.trafficInsurancePolicy,
                                            trafficInsuranceExpiry: value
                                        }
                                    }
                                });
                            }}
                            invalid={!!errors.trafficInsuranceExpiry}
                            disabled={!clientTrafficAccess.editable} // CustomerInfoForm ile aynı kullanım: !editable
                            required={clientTrafficAccess.required}
                        />
                        <FormFeedback>{errors.trafficInsuranceExpiry}</FormFeedback>
                    </Col>
                </Row>
            </FormGroup>

            <FormGroup>
                <Row>
                    <Col md={3}>
                        <FormGroup>
                            <Label className="form-label">Müvekkil Kasko Sağlayıcısı {clientCascoAccess.required && <span className="text-danger">*</span>}</Label>
                            <Input
                                type="select"
                                id="cascoInsuranceProvider"
                                name="cascoInsuranceProvider"
                                value={formData.cascoInsurancePolicy?.cascoInsuranceProvider?.[0] || ""}
                                onChange={handleClientCascoInsuranceProviderChange}
                                invalid={!!errors.cascoInsuranceProvider}
                                disabled={!clientCascoAccess.editable} // CustomerInfoForm ile aynı kullanım: !editable
                                required={clientCascoAccess.required}
                            >
                                <option value="">Sigorta Şirketi Seçiniz</option>
                                {insuranceCompanies && insuranceCompanies.length > 0 ?
                                    insuranceCompanies.map(company => (
                                        <option key={company.id} value={company.id}>
                                            {company.name}
                                        </option>
                                    )) :
                                    <option value="" disabled>Sigorta şirketi bulunamadı</option>
                                }
                            </Input>
                            <FormFeedback>{errors.cascoInsuranceProvider}</FormFeedback>
                        </FormGroup>
                    </Col>
                    <Col md={3}>
                        <Label for="cascoInsuranceNumber">Müvekkil Kasko Numarası {clientCascoAccess.required && <span className="text-danger">*</span>}</Label>
                        <Input
                            type="text"
                            id="cascoInsuranceNumber"
                            name="cascoInsuranceNumber"
                            value={formData.cascoInsurancePolicy?.cascoInsuranceNumber || ''}
                            onChange={handleClientCascoInsuranceNumberChange}
                            invalid={!!errors.cascoInsuranceNumber}
                            disabled={!clientCascoAccess.editable} // CustomerInfoForm ile aynı kullanım: !editable
                            required={clientCascoAccess.required}
                        />
                        <FormFeedback>{errors.cascoInsuranceNumber}</FormFeedback>
                    </Col>
                    <Col md={3}>
                        <Label for="cascoInsuranceStart">Müvekkil Kasko Başlangıç {clientCascoStartAccess.required && <span className="text-danger">*</span>}</Label>
                        <Input
                            type="date"
                            id="cascoInsuranceStart"
                            name="cascoInsuranceStart"
                            value={formData.cascoInsurancePolicy?.cascoInsuranceStart instanceof Date && !isNaN(formData.cascoInsurancePolicy?.cascoInsuranceStart)
                                ? formData.cascoInsurancePolicy?.cascoInsuranceStart.toISOString().substring(0, 10)
                                : ''}
                            onChange={(e) => {
                                const value = e.target.value ? new Date(e.target.value) : null;
                                handleInputChange({
                                    target: {
                                        name: 'cascoInsurancePolicy',
                                        value: {
                                            ...formData.cascoInsurancePolicy,
                                            cascoInsuranceStart: value
                                        }
                                    }
                                });
                            }}
                            invalid={!!errors.cascoInsuranceStart}
                            disabled={!clientCascoStartAccess.editable}
                            required={clientCascoStartAccess.required}
                        />
                        <FormFeedback>{errors.cascoInsuranceStart}</FormFeedback>
                    </Col>
                    <Col md={3}>
                        <Label for="cascoInsuranceExpiry">Müvekkil Kasko Vade {clientCascoAccess.required && <span className="text-danger">*</span>}</Label>
                        <Input
                            type="date"
                            id="cascoInsuranceExpiry"
                            name="cascoInsuranceExpiry"
                            value={formData.cascoInsurancePolicy?.cascoInsuranceExpiry instanceof Date && !isNaN(formData.cascoInsurancePolicy?.cascoInsuranceExpiry)
                                ? formData.cascoInsurancePolicy?.cascoInsuranceExpiry.toISOString().substring(0, 10)
                                : ''}
                            onChange={(e) => {
                                const value = e.target.value ? new Date(e.target.value) : null;
                                handleInputChange({
                                    target: {
                                        name: 'cascoInsurancePolicy',
                                        value: {
                                            ...formData.cascoInsurancePolicy,
                                            cascoInsuranceExpiry: value
                                        }
                                    }
                                });
                            }}
                            invalid={!!errors.cascoInsuranceExpiry}
                            disabled={!clientCascoAccess.editable} // CustomerInfoForm ile aynı kullanım: !editable
                            required={clientCascoAccess.required}
                        />
                        <FormFeedback>{errors.cascoInsuranceExpiry}</FormFeedback>
                    </Col>
                </Row>
            </FormGroup>

            <div style={{ height: "20px" }}></div>
            <H6>Karşı Taraf Sigorta Bilgileri</H6>
            <div style={{ height: "20px" }}></div>

            <Row form>
                <Col md={6}>
                    <FormGroup>
                        <Label for="counterpartyType">Karşı Taraf Türü {counterpartyTypeAccess.required && <span className="text-danger">*</span>}</Label>
                        <Input
                            type="select"
                            id="counterpartyType"
                            name="counterpartyType"
                            value={formData.counterpartyType}
                            onChange={handleCounterPartyTypeChange}
                            className="form-control"
                            disabled={!counterpartyTypeAccess.editable} // CustomerInfoForm ile aynı kullanım: !editable
                            required={counterpartyTypeAccess.required}
                        >
                            <option value="Bireysel">Bireysel</option>
                            <option value="Kurumsal">Kurumsal</option>
                        </Input>
                    </FormGroup>
                </Col>
                {formData.counterpartyType === 'Bireysel' && (
                    <Col md={6}>
                        <FormGroup>
                            <Label for="counterpartyIdentificationNumber">Karşı Taraf TC Kimlik Numarası {counterpartyIdAccess.required && <span className="text-danger">*</span>}</Label>
                            <Input
                                type="text"
                                id="counterpartyIdentificationNumber"
                                name="counterpartyIdentificationNumber"
                                value={formData.counterpartyIdentificationNumber}
                                onChange={(e) => {
                                    let value = e.target.value.replace(/[^0-9]/g, '');
                                    if (value.length > 11) {
                                        value = value.slice(0, 11);
                                    }
                                    handleInputChange({
                                        target: {
                                            name: 'counterpartyIdentificationNumber',
                                            value
                                        }
                                    });
                                }}
                                maxLength={11}
                                placeholder="11 haneli TC Kimlik No"
                                invalid={!!errors.counterpartyIdentificationNumber || (formData.counterpartyIdentificationNumber && formData.counterpartyIdentificationNumber.length !== 11)}
                                disabled={!counterpartyIdAccess.editable} // CustomerInfoForm ile aynı kullanım: !editable
                                required={counterpartyIdAccess.required}
                            />
                            <FormFeedback>
                                {errors.counterpartyIdentificationNumber || 'TC Kimlik Numarası 11 haneli olmalıdır'}
                            </FormFeedback>
                        </FormGroup>
                    </Col>
                )}
                {formData.counterpartyType === 'Kurumsal' && (
                    <Col md={6}>
                        <FormGroup>
                            <Label for="counterpartyTaxNumber">Karşı Taraf Vergi Numarası {counterpartyTaxAccess.required && <span className="text-danger">*</span>}</Label>
                            <Input
                                type="text"
                                id="counterpartyTaxNumber"
                                name="counterpartyTaxNumber"
                                value={formData.counterpartyTaxNumber}
                                onChange={handleInputChange}
                                invalid={!!errors.counterpartyTaxNumber}
                                disabled={!counterpartyTaxAccess.editable} // CustomerInfoForm ile aynı kullanım: !editable
                                required={counterpartyTaxAccess.required}
                            />
                            <FormFeedback>{errors.counterpartyTaxNumber}</FormFeedback>
                        </FormGroup>
                    </Col>
                )}
            </Row>

            <Row form>
                <Col md={6}>
                    <FormGroup>
                        <Label for="counterpartyCarOwner">Karşı Taraf Araç Sahibi {counterpartyOwnerAccess.required && <span className="text-danger">*</span>}</Label>
                        <Input
                            type="text"
                            id="counterpartyCarOwner"
                            name="counterpartyCarOwner"
                            value={formData.counterpartyCarOwner}
                            onChange={(e) => {
                                const value = e.target.value
                                    .replace(/[^a-zA-ZğüşıöçĞÜŞİÖÇ\s]/g, '') // Sadece harf ve boşluk (Türkçe karakterleri içerir)
                                    .replace(/^\s+/, '') // Baştaki boşlukları sil
                                    .replace(/\s{2,}/g, ' ') // Birden fazla boşluğu tek boşluğa çevir
                                    .split(' ')
                                    .map(word => word ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase() : '')
                                    .join(' ');
                                handleInputChange({
                                    target: {
                                        name: 'counterpartyCarOwner',
                                        value
                                    }
                                });
                            }}
                            placeholder="Ad Soyad"
                            invalid={!!errors.counterpartyCarOwner || (formData.counterpartyCarOwner &&
                                !/^[A-ZĞÜŞİÖÇa-zğüşıöç]+(\s[A-ZĞÜŞİÖÇa-zğüşıöç]+)+$/.test(formData.counterpartyCarOwner))}
                            disabled={!counterpartyOwnerAccess.editable} // CustomerInfoForm ile aynı kullanım: !editable
                            required={counterpartyOwnerAccess.required}
                        />
                        <FormFeedback>
                            {errors.counterpartyCarOwner || "Lütfen geçerli bir ad soyad giriniz (ör: Ahmet Yılmaz)"}
                        </FormFeedback>
                    </FormGroup>
                </Col>
                <Col md={6}>
                    <FormGroup>
                        <Label for="counterpartyLicensePlateNumber">Karşı Taraf Plaka {counterpartyPlateAccess.required && <span className="text-danger">*</span>}</Label>
                        <Input
                            type="text"
                            id="counterpartyLicensePlateNumber"
                            name="counterpartyLicensePlateNumber"
                            value={formData.counterpartyLicensePlateNumber}
                            onChange={(e) => {
                                const value = e.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
                                handleInputChange({
                                    target: {
                                        name: 'counterpartyLicensePlateNumber',
                                        value
                                    }
                                });
                            }}
                            placeholder="34ABC123"
                            invalid={!!errors.counterpartyLicensePlateNumber}
                            disabled={!counterpartyPlateAccess.editable} // CustomerInfoForm ile aynı kullanım: !editable
                            required={counterpartyPlateAccess.required}
                        />
                        <FormFeedback>{errors.counterpartyLicensePlateNumber}</FormFeedback>
                    </FormGroup>
                </Col>
            </Row>

            <FormGroup>
                <Label for="counterpartyAddress">Adres {counterpartyAddressAccess.required && <span className="text-danger">*</span>}</Label>
                <Input
                    type="textarea"
                    id="counterpartyAddress"
                    name="counterpartyAddress"
                    value={formData.counterpartyAddress}
                    onChange={handleInputChange}
                    invalid={!!errors.counterpartyAddress}
                    disabled={!counterpartyAddressAccess.editable} // CustomerInfoForm ile aynı kullanım: !editable
                    required={counterpartyAddressAccess.required}
                />
                <FormFeedback>{errors.counterpartyAddress}</FormFeedback>
            </FormGroup>            <FormGroup>
                <Label for="counterpartyFaultRate">Karşı Taraf Kusur Oranı {counterpartyFaultRateAccess.required && <span className="text-danger">*</span>}</Label>
                <div className="input-group">
                    <Input
                        type="text"
                        id="counterpartyFaultRate"
                        name="counterpartyFaultRate"
                        value={formData.counterpartyFaultRate || ''}
                        placeholder="Otomatik hesaplanır"
                        invalid={!!errors.counterpartyFaultRate}
                        disabled={true} // Bu alan otomatik hesaplandığı için hep disabled olmalı
                    />
                    <div className="input-group-append">
                        <span className="input-group-text">%</span>
                    </div>
                    <FormFeedback>
                        {errors.counterpartyFaultRate}
                    </FormFeedback>
                </div>
            </FormGroup>

            <FormGroup>
                <Row>
                    <Col md={3}>
                        <FormGroup>
                            <Label className="form-label">Karşı Taraf Trafik Sigorta Sağlayıcısı {cpTrafficAccess.required && <span className="text-danger">*</span>}</Label>
                            <Input
                                type="select"
                                id="counterpartyTrafficInsuranceProvider"
                                name="counterpartyTrafficInsuranceProvider"
                                value={formData.counterpartyTrafficInsurancePolicy?.trafficInsuranceProvider?.[0] || ""}
                                onChange={handleCounterpartyTrafficInsuranceProviderChange}
                                invalid={!!errors.counterpartyTrafficInsuranceProvider}
                                disabled={!cpTrafficAccess.editable} // CustomerInfoForm ile aynı kullanım: !editable
                                required={cpTrafficAccess.required}
                            >
                                <option value="">Sigorta Şirketi Seçiniz</option>
                                {insuranceCompanies && insuranceCompanies.length > 0 ?
                                    insuranceCompanies.map(company => (
                                        <option key={company.id} value={company.id}>
                                            {company.name}
                                        </option>
                                    )) :
                                    <option value="" disabled>Sigorta şirketi bulunamadı</option>
                                }
                            </Input>
                            <FormFeedback>{errors.counterpartyTrafficInsuranceProvider}</FormFeedback>
                        </FormGroup>
                    </Col>
                    <Col md={3}>
                        <Label for="counterpartyTrafficInsuranceNumber">Karşı Taraf Trafik Sigorta Numarası {cpTrafficAccess.required && <span className="text-danger">*</span>}</Label>
                        <Input
                            type="text"
                            id="counterpartyTrafficInsuranceNumber"
                            name="counterpartyTrafficInsuranceNumber"
                            value={formData.counterpartyTrafficInsurancePolicy?.trafficInsuranceNumber || ''}
                            onChange={handleCounterpartyTrafficInsuranceNumberChange}
                            invalid={!!errors.counterpartyTrafficInsuranceNumber}
                            disabled={!cpTrafficAccess.editable} // CustomerInfoForm ile aynı kullanım: !editable
                            required={cpTrafficAccess.required}
                        />
                        <FormFeedback>{errors.counterpartyTrafficInsuranceNumber}</FormFeedback>
                    </Col>
                    <Col md={3}>
                        <Label for="counterpartyTrafficInsuranceStart">Karşı Taraf Trafik Başlangıç {cpTrafficStartAccess.required && <span className="text-danger">*</span>}</Label>
                        <Input
                            type="date"
                            id="counterpartyTrafficInsuranceStart"
                            name="counterpartyTrafficInsuranceStart"
                            value={formData.counterpartyTrafficInsurancePolicy?.trafficInsuranceStart instanceof Date && !isNaN(formData.counterpartyTrafficInsurancePolicy?.trafficInsuranceStart)
                                ? formData.counterpartyTrafficInsurancePolicy?.trafficInsuranceStart.toISOString().substring(0, 10)
                                : ''}
                            onChange={(e) => {
                                const value = e.target.value ? new Date(e.target.value) : null;
                                handleInputChange({
                                    target: {
                                        name: 'counterpartyTrafficInsurancePolicy',
                                        value: {
                                            ...formData.counterpartyTrafficInsurancePolicy,
                                            trafficInsuranceStart: value
                                        }
                                    }
                                });
                            }}
                            invalid={!!errors.counterpartyTrafficInsuranceStart}
                            disabled={!cpTrafficStartAccess.editable}
                            required={cpTrafficStartAccess.required}
                        />
                        <FormFeedback>{errors.counterpartyTrafficInsuranceStart}</FormFeedback>
                    </Col>
                    <Col md={3}>
                        <Label for="counterpartyTrafficInsuranceExpiry">Karşı Taraf Trafik Sigorta Vade {cpTrafficAccess.required && <span className="text-danger">*</span>}</Label>
                        <Input
                            type="date"
                            id="counterpartyTrafficInsuranceExpiry"
                            name="counterpartyTrafficInsuranceExpiry"
                            value={formData.counterpartyTrafficInsurancePolicy?.trafficInsuranceExpiry instanceof Date && !isNaN(formData.counterpartyTrafficInsurancePolicy?.trafficInsuranceExpiry)
                                ? formData.counterpartyTrafficInsurancePolicy?.trafficInsuranceExpiry.toISOString().substring(0, 10)
                                : ''}
                            onChange={(e) => {
                                const value = e.target.value ? new Date(e.target.value) : null;
                                handleInputChange({
                                    target: {
                                        name: 'counterpartyTrafficInsurancePolicy',
                                        value: {
                                            ...formData.counterpartyTrafficInsurancePolicy,
                                            trafficInsuranceExpiry: value
                                        }
                                    }
                                });
                            }}
                            invalid={!!errors.counterpartyTrafficInsuranceExpiry}
                            disabled={!cpTrafficAccess.editable} // CustomerInfoForm ile aynı kullanım: !editable
                            required={cpTrafficAccess.required}
                        />
                        <FormFeedback>{errors.counterpartyTrafficInsuranceExpiry}</FormFeedback>
                    </Col>
                </Row>
            </FormGroup>

            <FormGroup>
                <Row>
                    <Col md={3}>
                        <FormGroup>
                            <Label className="form-label">Karşı Taraf Kasko Sağlayıcısı {cpCascoAccess.required && <span className="text-danger">*</span>}</Label>
                            <Input
                                type="select"
                                id="counterpartyCascoInsuranceProvider"
                                name="counterpartyCascoInsuranceProvider"
                                value={formData.counterpartyCascoInsurancePolicy?.cascoInsuranceProvider?.[0] || ""}
                                onChange={handleCounterpartyCascoInsuranceProviderChange}
                                invalid={!!errors.counterpartyCascoInsuranceProvider}
                                disabled={!cpCascoAccess.editable} // CustomerInfoForm ile aynı kullanım: !editable
                                required={cpCascoAccess.required}
                            >
                                <option value="">Sigorta Şirketi Seçiniz</option>
                                {insuranceCompanies && insuranceCompanies.length > 0 ?
                                    insuranceCompanies.map(company => (
                                        <option key={company.id} value={company.id}>
                                            {company.name}
                                        </option>
                                    )) :
                                    <option value="" disabled>Sigorta şirketi bulunamadı</option>
                                }
                            </Input>
                            <FormFeedback>{errors.counterpartyCascoInsuranceProvider}</FormFeedback>
                        </FormGroup>
                    </Col>
                    <Col md={3}>
                        <Label for="counterpartyCascoInsuranceNumber">Karşı Taraf Kasko Numarası {cpCascoAccess.required && <span className="text-danger">*</span>}</Label>
                        <Input
                            type="text"
                            id="counterpartyCascoInsuranceNumber"
                            name="counterpartyCascoInsuranceNumber"
                            value={formData.counterpartyCascoInsurancePolicy?.cascoInsuranceNumber || ''}
                            onChange={handleCounterpartyCascoInsuranceNumberChange}
                            invalid={!!errors.counterpartyCascoInsuranceNumber}
                            disabled={!cpCascoAccess.editable} // CustomerInfoForm ile aynı kullanım: !editable
                            required={cpCascoAccess.required}
                        />
                        <FormFeedback>{errors.counterpartyCascoInsuranceNumber}</FormFeedback>
                    </Col>
                    <Col md={3}>
                        <Label for="counterpartyCascoInsuranceStart">Karşı Taraf Kasko Başlangıç {cpCascoStartAccess.required && <span className="text-danger">*</span>}</Label>
                        <Input
                            type="date"
                            id="counterpartyCascoInsuranceStart"
                            name="counterpartyCascoInsuranceStart"
                            value={formData.counterpartyCascoInsurancePolicy?.cascoInsuranceStart instanceof Date && !isNaN(formData.counterpartyCascoInsurancePolicy?.cascoInsuranceStart)
                                ? formData.counterpartyCascoInsurancePolicy?.cascoInsuranceStart.toISOString().substring(0, 10)
                                : ''}
                            onChange={(e) => {
                                const value = e.target.value ? new Date(e.target.value) : null;
                                handleInputChange({
                                    target: {
                                        name: 'counterpartyCascoInsurancePolicy',
                                        value: {
                                            ...formData.counterpartyCascoInsurancePolicy,
                                            cascoInsuranceStart: value
                                        }
                                    }
                                });
                            }}
                            invalid={!!errors.counterpartyCascoInsuranceStart}
                            disabled={!cpCascoStartAccess.editable}
                            required={cpCascoStartAccess.required}
                        />
                        <FormFeedback>{errors.counterpartyCascoInsuranceStart}</FormFeedback>
                    </Col>
                    <Col md={3}>
                        <Label for="counterpartyCascoInsuranceExpiry">Karşı Taraf Kasko Vade {cpCascoAccess.required && <span className="text-danger">*</span>}</Label>
                        <Input
                            type="date"
                            id="counterpartyCascoInsuranceExpiry"
                            name="counterpartyCascoInsuranceExpiry"
                            value={formData.counterpartyCascoInsurancePolicy?.cascoInsuranceExpiry instanceof Date && !isNaN(formData.counterpartyCascoInsurancePolicy?.cascoInsuranceExpiry)
                                ? formData.counterpartyCascoInsurancePolicy?.cascoInsuranceExpiry.toISOString().substring(0, 10)
                                : ''}
                            onChange={(e) => {
                                const value = e.target.value ? new Date(e.target.value) : null;
                                handleInputChange({
                                    target: {
                                        name: 'counterpartyCascoInsurancePolicy',
                                        value: {
                                            ...formData.counterpartyCascoInsurancePolicy,
                                            cascoInsuranceExpiry: value
                                        }
                                    }
                                });
                            }}
                            invalid={!!errors.counterpartyCascoInsuranceExpiry}
                            disabled={!cpCascoAccess.editable} // CustomerInfoForm ile aynı kullanım: !editable
                            required={cpCascoAccess.required}
                        />
                        <FormFeedback>{errors.counterpartyCascoInsuranceExpiry}</FormFeedback>
                    </Col>
                </Row>
            </FormGroup>

            <div style={{ height: "20px" }}></div>
            <hr className="mt-4 mb-4" />
            <div style={{ height: "20px" }}></div>

            <Col>
                <div className='m-t-15 m-checkbox-inline custom-radio-ml'>
                    {/* TODO: Bu checkbox'ların değerleri ve disabled durumları yönetilmeli */}
                    <div className='checkbox checkbox-dark'>
                        <Input id='inline-1' type='checkbox' />
                        <Label for='inline-1'>
                            Resim
                        </Label>
                    </div>
                    <div className='checkbox checkbox-dark'>
                        <Input id='inline-2' type='checkbox' />
                        <Label for='inline-2'>
                            KM
                        </Label>
                    </div>
                    <div className='checkbox checkbox-dark'>
                        <Input id='inline-3' type='checkbox' />
                        <Label for='inline-3'>
                            Ruhsat
                        </Label>
                    </div>
                    <div className='checkbox checkbox-dark'>
                        <Input id='inline-4' type='checkbox' />
                        <Label for='inline-4'>
                            KTT
                        </Label>
                    </div>
                    <div className='checkbox checkbox-dark'>
                        <Input id='inline-5' type='checkbox' />
                        <Label for='inline-5'>
                            Poliçe
                        </Label>
                    </div>
                    <div className='checkbox checkbox-dark'>
                        <Input id='inline-6' type='checkbox' />
                        <Label for='inline-6'>
                            Fatura
                        </Label>
                    </div>
                    <div className='checkbox checkbox-dark'>
                        <Input id='inline-7' type='checkbox' />
                        <Label for='inline-7'>
                            Eksper Raporu
                        </Label>
                    </div>
                    <div className='checkbox checkbox-dark'>
                        <Input id='inline-8' type='checkbox' />
                        <Label for='inline-8'>
                            Kusur Durumu Gösteri Belgesi
                        </Label>
                    </div>
                </div>
            </Col>
        </>
    );
};

DocumentRequestForm.propTypes = {
    formData: PropTypes.object.isRequired,
    errors: PropTypes.object.isRequired,
    setFormData: PropTypes.func.isRequired,
    handleInputChange: PropTypes.func.isRequired,
    handleCounterPartyTypeChange: PropTypes.func.isRequired,
    workFileTypes: PropTypes.array.isRequired,
    insuranceCompanies: PropTypes.array.isRequired,
    fieldPermissions: PropTypes.array,
    canEdit: PropTypes.bool,
};

export default DocumentRequestForm;
